import { useEffect, useState } from "react";
import { api } from "../lib/api";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "body", label: "Body & Health" },
  { id: "style", label: "Style & Image" },
  { id: "mind", label: "Etiquette & Mind" },
];

export default function Services({ onBook }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/services").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const shown = filter === "all" ? items : items.filter((s) => s.category === filter);

  return (
    <section id="services" data-testid="services-section" className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="eyebrow">The 7 Pillars</div>
            <h2 className="font-display text-4xl sm:text-5xl uppercase mt-2">
              Every service. <span className="gold-text">One standard.</span>
            </h2>
            <p className="text-[var(--text-secondary)] max-w-xl mt-3">
              Choose the pillar you want to level up — or stack them into your full transformation blueprint.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={`services-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest transition-colors ${
                  filter === f.id
                    ? "bg-[var(--gold)] text-[#0a0a0e] border border-[var(--gold)]"
                    : "border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:text-[var(--gold)]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {shown.map((s, idx) => {
            const Icon = Icons[s.icon] || Icons.Star;
            return (
              <div
                key={s.id}
                data-testid={`service-card-${s.id}`}
                className="gr-card p-6 md:p-7 flex flex-col gr-rise"
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 rounded-xl bg-[rgba(212,175,55,0.1)] border border-[var(--border-gold)] grid place-items-center text-[var(--gold)]">
                    <Icon size={22} strokeWidth={1.8} />
                  </div>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-5 font-display text-2xl uppercase leading-tight">{s.name}</h3>
                <p className="text-[var(--text-secondary)] mt-2 text-sm">{s.tagline}</p>
                <ul className="mt-4 space-y-1.5">
                  {s.features.map((f) => (
                    <li key={f} className="text-sm text-[var(--text-secondary)] flex gap-2">
                      <span className="text-[var(--gold)]">›</span> {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-5 rounded-lg border border-[var(--border-subtle)] bg-[rgba(212,175,55,0.04)] px-3 py-2">
                  <div className="text-[10px] uppercase tracking-widest font-mono text-[var(--gold)] mb-1">
                    Dev&apos;s insider tip
                  </div>
                  <div className="text-xs text-[var(--text-secondary)] italic">&ldquo;{s.dev_tip}&rdquo;</div>
                </div>
                <button
                  data-testid={`service-book-btn-${s.id}`}
                  onClick={() => {
                    onBook && onBook(s.id);
                    toast.success(`${s.name} selected — pick a date below`);
                  }}
                  className="mt-6 gr-btn-ghost py-2.5 rounded-full text-sm"
                >
                  Book This Service
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
